/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface ConnectionVersionsRequest {
  role: string;
  counterPartyId: string;
  counterCountryCode: string;
  counterRole: string;
  limit?: number;
}

interface FetchConnectionVersionsResponse {
  results: any[];
  limit: number;
  page: number;
}

/**
 * Fetches the OCPI version list cached on the connection between our tenant
 * (resolved server-side from the auth token) and the given counterparty.
 */
export const fetchConnectionVersions = createAsyncThunk<
  FetchConnectionVersionsResponse,
  ConnectionVersionsRequest
>(
  'ocpi-versions/fetchConnectionVersions',
  async (
    { role, counterPartyId, counterCountryCode, counterRole, limit },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalConnectionVersions(
        role,
        counterPartyId,
        counterCountryCode,
        counterRole,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      return {
        results: Array.isArray(results) ? results : [],
        limit: resolvedLimit,
        page: DefaultApiParameters.page,
      };
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.status_message ||
          error?.response?.data?.message ||
          'Failed to fetch OCPI version list.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Resolves the filter list for the versions table. Filter options are known upfront
 * (there is no backend filter-list endpoint for OCPI versions), so this only exists to
 * satisfy CustomTable's filterListToDispatch contract.
 */
export const fetchConnectionVersionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'ocpi-versions/fetchConnectionVersionFilters',
  async () => availableFilters,
);

interface OCPIVersionsInitialState {
  versions: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: string;
  sortField: string;
}

const initialState: OCPIVersionsInitialState = {
  versions: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const ocpiVersionsSlice = createSlice({
  name: 'ocpi-versions',
  initialState,
  reducers: {
    /**
     * Update OCPI Versions filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateVersionsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableVersionsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionVersions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConnectionVersions.fulfilled, (state, action) => {
        state.loading = false;
        state.versions = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.results.length;
        state.totalPages = Math.max(
          1,
          Math.ceil(action.payload.results.length / action.payload.limit),
        );
      })
      .addCase(fetchConnectionVersions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchConnectionVersionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchConnectionVersionFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchConnectionVersionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateVersionsFilters, disableVersionsFilterApplied, setSortData } =
  ocpiVersionsSlice.actions;

export default ocpiVersionsSlice.reducer;
